<template>
  <div>
    <div
      v-if="loading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        size="80"
        color="#2661FF"
      ></v-progress-circular>
    </div>
    <div v-else>
      <v-row no-gutters class="pt-4">
        <v-col cols="12" md="3">
          <h4 class="text-h6 grey--text text--darken-3 font-weight-medium">
            {{ $t('inbox.table.title') }}
          </h4>
        </v-col>
        <v-col cols="12" md="9" class="d-flex justify-end align-center">
          <v-btn
            color="#2661FF"
            class="rounded-lg"
            @click="openCreateMessageDrawer(true)"
          >
            <span class="white--text">
              <v-icon>
                add
              </v-icon>
              {{ $t('inbox.add.title') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="custom_card" flat>
            <v-card-text>
              <v-row no-gutters>
                <v-col cols="12" lg="3" class="py-2">
                  <v-text-field
                    v-model="inboxsTable.filters.search"
                    :label="$t('inbox.table.search')"
                    append-icon="search"
                    class="rounded-lg"
                    outlined
                    dense
                    single-line
                    hide-details
                    clearable
                    @keyup.enter="fetchInboxMessages"
                    @click:clear="clearSearch"
                  />
                </v-col>
                <v-col cols="12" lg="9" class="d-flex justify-end py-2">
                  <DateBar
                    :dateRange="inboxsTable.filters.dateRange"
                    @updateRange="
                      (value, startDate, index) => {
                        inboxsTable.filters.dateRange = index
                        inboxsTable.filters.endDate = $moment()
                          .add(1, 'days')
                          .format('YYYY-MM-DD')
                        changeDates(startDate, inboxsTable.filters.endDate)
                      }
                    "
                    format-date="YYYY-MM-DD"
                  />
                  <DateFilters
                    :initialDate="inboxsTable.filters.startDate"
                    :finalDate="inboxsTable.filters.endDate"
                    @updateInitialDate="updateDates('startDate', $event)"
                    @updateFinalDate="updateDates('endDate', $event)"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :loading="inboxsTable.loading"
                    :headers="inboxsTable.headers"
                    :items="inboxsTable.items"
                    :options="inboxsTable.pagination"
                    :footer-props="{
                      'items-per-page-options': [10, 20, 30, 40, 50]
                    }"
                    :items-per-page="inboxsTable.pagination.rowsPerPage"
                    :server-items-length="inboxsTable.total"
                    :no-result-text="$t('inbox.table.no_result')"
                    @update:options="inboxsTable.pagination = $event"
                  >
                    <template v-slot:item.name="{ item }">
                      <div class="mt-4">
                        <p class="subtitle-2 grey--text text--darken-3">
                          {{ item.name }}
                        </p>
                      </div>
                    </template>
                    <template v-slot:item.description="{ item }">
                      <div class="mt-4">
                        <p>
                          {{ truncateDescription(item.description) }}
                        </p>
                      </div>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      <div class="mt-4">
                        <p>
                          {{ item.created_at | moment('lll') }}
                        </p>
                      </div>
                    </template>
                    <template v-slot:item.organization="{ item }">
                      <div class="mt-4">
                        <p>
                          {{ item.organization ? item.organization.name : '-' }}
                        </p>
                      </div>
                    </template>
                    <template v-slot:item.is_completed="{ item }">
                      <v-chip
                        :color="item.is_completed ? 'success' : 'error'"
                        text-color="white"
                        class="rounded-lg"
                        :disabled="item.isLoading"
                        @click="updateStatus(item)"
                      >
                        {{
                          item.is_completed
                            ? $t('inbox.table.completed')
                            : $t('inbox.table.not_completed')
                        }}
                      </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex">
                        <v-btn icon @click="openInboxMessageDetails(item)">
                          <v-icon>
                            visibility
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <CreateInboxMessage v-if="isCreateInboxMessageModalOpen" />
    <ViewInboxMessageDialog v-if="isViewInboxMessageModalOpen" />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import DateBar from '@/components/Common/DateBar/Index.vue'
import DateFilters from '@/components/Common/DateFilters/Index.vue'
import CreateInboxMessage from '@/components/Inbox/CreateInboxMessageDialog.vue'
import ViewInboxMessageDialog from '@/components/Inbox/ViewInboxMessageDialog.vue'

export default {
  name: 'Inbox',
  components: {
    DateBar,
    DateFilters,
    CreateInboxMessage,
    ViewInboxMessageDialog
  },
  data() {
    return {
      loading: false,
      inboxsTable: {
        loading: false,
        items: [],
        total: 0,
        filters: {
          search: '',
          dateRange: 3,
          startDate: this.$moment()
            .startOf('month')
            .format('YYYY-MM-DD'),
          endDate: this.$moment()
            .endOf('day')
            .add(1, 'days')
            .format('YYYY-MM-DD')
        },
        pagination: {
          page: 1,
          sortBy: ['created_at'],
          sortDesc: [true],
          rowsPerPage: 10
        },
        headers: [
          {
            text: 'ID',
            align: 'center',
            sortable: true,
            value: 'id',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('inbox.table.headers.action'),
            align: 'center',
            sortable: true,
            value: 'name',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('inbox.table.headers.reason'),
            align: 'center',
            sortable: false,
            value: 'description',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('common.created_at'),
            align: 'center',
            sortable: true,
            value: 'created_at',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('inbox.table.headers.organization'),
            align: 'center',
            sortable: false,
            value: 'organization',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('inbox.table.headers.status'),
            align: 'center',
            sortable: false,
            value: 'is_completed',
            class: 'text-no-wrap header_color'
          },
          {
            text: this.$t('common.actions'),
            sortable: false,
            value: 'actions',
            class: 'text-no-wrap header_color'
          }
        ]
      }
    }
  },
  async created() {
    this.loading = true
    await this.fetchOrganizations()
    this.loading = false
  },
  watch: {
    'inboxsTable.pagination': {
      async handler() {
        await this.fetchInboxMessages()
      },
      deep: true
    },
    updateRequired: {
      async handler() {
        if (this.updateRequired) {
          await this.fetchInboxMessages()
          this.setUpdateRequired(false)
        }
      }
    }
  },
  computed: {
    ...mapState({
      messages: state => state.inbox.messages,
      totalMessages: state => state.inbox.totalMessages,
      isCreateInboxMessageModalOpen: state =>
        state.inbox.isCreateInboxMessageModalOpen,
      isViewInboxMessageModalOpen: state =>
        state.inbox.isViewInboxMessageModalOpen,
      updateRequired: state => state.inbox.updateRequired
    })
  },
  methods: {
    ...mapActions({
      getInboxMessages: 'inbox/getInboxMessages',
      updateInboxMessage: 'inbox/updateInboxMessage',
      fetchOrganizations: 'organization/getAll'
    }),
    ...mapMutations({
      setMessage: 'inbox/setMessage',
      setUpdateRequired: 'inbox/setUpdateRequired',
      toggleCreateInboxMessageModal: 'inbox/toggleCreateInboxMessageModal',
      toggleViewInboxMessageModal: 'inbox/toggleViewInboxMessageModal'
    }),
    async changeDates(startDate, endDate) {
      this.inboxsTable.filters.startDate = startDate
      this.inboxsTable.filters.endDate = endDate
      await this.fetchInboxMessages()
    },
    async updateDates(property, date) {
      this.inboxsTable.filters[property] = date
      this.inboxsTable.filters.dateRange = 7
      await this.changeDates(
        this.inboxsTable.filters.startDate,
        this.inboxsTable.filters.endDate
      )
    },
    async fetchInboxMessages() {
      const { inboxsTable } = this
      const { page, rowsPerPage, sortBy, sortDesc } = inboxsTable.pagination
      const { startDate, endDate, search } = inboxsTable.filters

      inboxsTable.loading = true

      await this.getInboxMessages({
        startDate,
        endDate,
        page,
        search,
        rowsPerPage,
        sortBy,
        sortDesc
      })

      inboxsTable.items = this.messages
      inboxsTable.total = Number(this.totalMessages)

      inboxsTable.loading = false
    },
    async clearSearch() {
      this.inboxsTable.filters.search = null
      await this.fetchInboxMessages()
    },
    async updateStatus(inboxMessage) {
      const { id, is_completed } = inboxMessage

      const foundMessage = this.inboxsTable.items.find(
        message => message.id === id
      )

      foundMessage.isLoading = true

      const { success } = await this.updateInboxMessage({
        id,
        is_completed: !is_completed
      })

      if (success) {
        foundMessage.is_completed = !is_completed
      }

      foundMessage.isLoading = false
    },
    truncateDescription(description) {
      const maxLength = 50 // Set your desired maximum length here
      return description.length > maxLength
        ? description.substring(0, maxLength) + '...'
        : description
    },
    openCreateMessageDrawer(value) {
      this.toggleCreateInboxMessageModal(value)
    },
    openInboxMessageDetails(item) {
      this.setMessage(item)
      this.toggleViewInboxMessageModal(true)
    }
  }
}
</script>

<style lang="scss">
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.header_color {
  color: #2661ff !important;
}
</style>
